// Where each editable value sits in the rendered RFQ email.
// apps/smart-catalog/frontend/src/components/buyer-agent/rfq-center/emailMarks.ts
//
// The preview is the real template, rendered by the backend into a sandboxed
// frame, so the page cannot ask it "which text is the greeting?". For the
// preview only, each editable value is wrapped in invisible characters that
// carry its number. After the frame loads, the page reads the frame's text,
// finds the wrapped values and measures them, and draws the click areas on
// top. The email that is sent never carries the marks.

const EDGE = '⁣';
const ZERO = '‌';
const ONE = '‍';
const BITS = 7;
const ANY_MARK = /[⁣‌‍]/g;

/** Wrap a value so it can be found again in the rendered email. `id` is 0..127. */
export function markValue(id: number, text: string): string {
  let bits = '';
  for (let b = BITS - 1; b >= 0; b -= 1) bits += (id >> b) & 1 ? ONE : ZERO;
  return `${EDGE}${bits}${EDGE}${text}${EDGE}${EDGE}`;
}

export function stripMarks(text: string): string {
  return text.replace(ANY_MARK, '');
}

export interface MarkBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

export interface FoundMark {
  id: number;
  /** One box per rendered line of the value, in the frame's own coordinates. */
  boxes: MarkBox[];
}

/** Every marked value in the document, measured. A value may run over several text nodes (line breaks). */
export function findMarks(doc: Document): FoundMark[] {
  const found: FoundMark[] = [];
  if (!doc.body) return found;
  const walker = doc.createTreeWalker(doc.body, NodeFilter.SHOW_TEXT);
  let open: { id: number; node: Text; offset: number } | null = null;
  for (let node = walker.nextNode() as Text | null; node; node = walker.nextNode() as Text | null) {
    const text = node.data;
    let at = text.indexOf(EDGE);
    while (at !== -1) {
      if (text[at + 1] === EDGE) {
        // The end of a value.
        if (open) {
          const range = doc.createRange();
          range.setStart(open.node, open.offset);
          range.setEnd(node, at);
          const boxes = Array.from(range.getClientRects())
            .filter((r) => r.width > 0 && r.height > 0)
            .map((r) => ({ left: r.left, top: r.top, width: r.width, height: r.height }));
          if (boxes.length) found.push({ id: open.id, boxes });
          open = null;
        }
        at = text.indexOf(EDGE, at + 2);
        continue;
      }
      const close = text.indexOf(EDGE, at + 1);
      if (close === -1) break;
      const bits = text.slice(at + 1, close);
      if (bits.length === BITS && /^[‌‍]+$/.test(bits)) {
        let id = 0;
        for (const bit of bits) id = (id << 1) | (bit === ONE ? 1 : 0);
        open = { id, node, offset: close + 1 };
      }
      at = text.indexOf(EDGE, close + 1);
    }
  }
  return found;
}

/** The box around all lines of a value. */
export function boundsOf(boxes: MarkBox[]): MarkBox {
  const left = Math.min(...boxes.map((b) => b.left));
  const top = Math.min(...boxes.map((b) => b.top));
  const right = Math.max(...boxes.map((b) => b.left + b.width));
  const bottom = Math.max(...boxes.map((b) => b.top + b.height));
  return { left, top, width: right - left, height: bottom - top };
}
